/**
 * Tab "Học liệu" — read-only material list for teacher during live session.
 * Click: file/document/audio/slide/video → fetch presigned URL → open new tab.
 *        link → open url directly.
 */
import { toast } from 'sonner';
import { useState } from 'react';
import { PackageOpen } from 'lucide-react';
import { MaterialRow } from './material-row';
import { useMaterialDownloadUrl } from '@client/lib/queries/use-material-download';
import type { DashboardMaterial } from '@client/lib/queries/use-session-dashboard';

interface TabMaterialsProps {
  materials: DashboardMaterial[];
}

export function TabMaterials({ materials }: TabMaterialsProps) {
  const downloadUrl = useMaterialDownloadUrl();
  // Track which materialId is currently loading
  const [loadingId, setLoadingId] = useState<string | null>(null);

  async function handleOpen(material: DashboardMaterial) {
    // Link type — open url directly (no presigned URL needed)
    if (material.type === 'link' && material.url) {
      window.open(material.url, '_blank', 'noopener,noreferrer');
      return;
    }

    // File/media types — fetch presigned URL first
    setLoadingId(material.id);
    try {
      const result = await downloadUrl.mutateAsync(material.id);
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Không thể mở tài liệu. Vui lòng thử lại.');
    } finally {
      setLoadingId(null);
    }
  }

  if (materials.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <PackageOpen className="h-8 w-8 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">Buổi học này chưa có học liệu.</p>
      </div>
    );
  }

  const sorted = [...materials].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="flex flex-col gap-2">
      {sorted.map((m) => (
        <MaterialRow
          key={m.id}
          material={m}
          onOpen={handleOpen}
          isLoading={loadingId === m.id}
        />
      ))}
    </div>
  );
}
